import {useMemo, useState} from 'preact/hooks';
import {
    useServicesParam,
    useFeaturesParam, useClearServicesAndFeaturesParams
} from '../../state/parameterStore.js'
import { toggleValue, countSelected } from '../../state/utils.js'
import Box from '@mui/material/Box';

import colors from '../colors';
import Loading from '../Loading';
import { LauncherFeatureSelectorTabs, TABS } from './LauncherFeatureSelectorTabs';
import LauncherFeatureStack from './LauncherFeatureStack';
import LauncherFeatureFilterField from './LauncherFeatureFilterField';
import LauncherFeatureFilterCheckbox from './LauncherFeatureFilterCheckbox';
import LauncherFeatureClearButton from "./LauncherFeatureClearButton";

// Create global properties and use them to prevent unnecessary re-rendering
let featureCallback, serviceCallback;

const LauncherFeatureSelector = () => {
    const [selectedServices, setSelectedServices, services, servicesLoading] = useServicesParam();
    const [selectedFeatures, setSelectedFeatures, , featureCategoryMap, featuresLoading] = useFeaturesParam();
    const categories = useMemo(() => Object.keys(featureCategoryMap).sort(), [featureCategoryMap]);

    featureCallback = (value) => {
        setSelectedFeatures(toggleValue(selectedFeatures, value));
    }
    serviceCallback = (value) => {
        setSelectedServices(toggleValue(selectedServices, value));
    }

    const [tab, setTab] = useState(TABS.TAB_SERVICES);
    const [featuresFilter, setFeaturesFilter] = useState('');
    const [doShowSupportedFeatures, setShowSupportedFeatures] = useState(true);

    const nSelectedFeatures = useMemo(() => countSelected(selectedFeatures), [selectedFeatures]);
    const nSelectedServices = useMemo(() => countSelected(selectedServices), [selectedServices]);
    const nSelected = nSelectedFeatures + nSelectedServices;

    const clearServicesAndFeaturesParams = useClearServicesAndFeaturesParams();

    const clearSelected = useMemo(() => (() => {
        if (tab === TABS.TAB_SERVICES) {
            setSelectedServices({});
        } else if (tab === TABS.TAB_FEATURES) {
            setSelectedFeatures({});
        } else {
            setSelectedServices({});
            setSelectedFeatures({});
            // Handle a special case
            clearServicesAndFeaturesParams();
        }
    }), [tab, setSelectedFeatures, setSelectedServices, clearServicesAndFeaturesParams]);

    const selectedFeatureObjs = useMemo(() => {
        const selectedFeatureObjs = [];
        for (let category of categories) {
            for (let feature of featureCategoryMap[category]) {
                if (selectedFeatures[feature.name]) {
                    selectedFeatureObjs.push(feature);
                }
            }
        }
        return selectedFeatureObjs;
    }, [selectedFeatures, featureCategoryMap, categories]);

    const selectedServiceObjs = useMemo(
        () => services ? services.filter(f => selectedServices[f.name]) : [],
        [selectedServices, services]
    );

    const handleFeatureClick = useMemo(() => (value) => featureCallback(value), []);
    const handleServiceClick = useMemo(() => (value) => serviceCallback(value), []);

    const handleTabChange = (e) => {
        // Clear the search in case of tab change
        setFeaturesFilter('');
        setTab(e.target.value);
    }

    return (
        <Box
            sx={{
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: colors.featureSelectorBorder,
                borderRadius: '8px',
                backgroundColor: colors.featureSelectorBackground,
                overflow: 'hidden',
                marginTop: '65px',
                marginBottom: '40px',
                paddingBottom: '60px'
            }}
        >
            <LauncherFeatureSelectorTabs tab={tab} handleTabChange={handleTabChange} nSelected={nSelected} />

            <LauncherFeatureFilterField value={featuresFilter} setValue={setFeaturesFilter} />
            { tab === TABS.TAB_FEATURES &&
                <LauncherFeatureFilterCheckbox selected={doShowSupportedFeatures}
                    setSelected={setShowSupportedFeatures} label="Only show GDK-tested features" />
            }

            <LauncherFeatureClearButton clickCallback={clearSelected} label={
                tab === TABS.TAB_FEATURES ? "Clear Selected Features" :
                    tab === TABS.TAB_SERVICES ? "Clear Selected Services" : "Clear All Selected" }
            />

            {
                (
                    (tab === TABS.TAB_SELECTED && (servicesLoading || featuresLoading)) ||
                    (tab === TABS.TAB_SERVICES && servicesLoading) ||
                    (tab === TABS.TAB_FEATURES && featuresLoading)
                ) &&
                <Loading />
            }

            {!servicesLoading && tab === TABS.TAB_SERVICES &&
                <LauncherFeatureStack features={services} selectedFeatures={selectedServices}
                    onClick={handleServiceClick} doShowServices={true} featuresFilter={featuresFilter} />
            }
            {!featuresLoading && tab === TABS.TAB_FEATURES &&
                categories.map(category =>
                    <LauncherFeatureStack features={featureCategoryMap[category]} selectedFeatures={selectedFeatures}
                        onClick={handleFeatureClick} featuresFilter={featuresFilter}
                        title={category} key={category} doShowSupportedFeatures={doShowSupportedFeatures} />
                )
            }
            {!servicesLoading && tab === TABS.TAB_SELECTED &&
                <LauncherFeatureStack features={selectedServiceObjs} selectedFeatures={selectedServices}
                    onClick={handleServiceClick} doShowServices={true} featuresFilter={featuresFilter}
                    title="Cloud Services" showEmpty={true} />
            }
            {!featuresLoading && tab === TABS.TAB_SELECTED &&
                <LauncherFeatureStack features={selectedFeatureObjs} selectedFeatures={selectedFeatures}
                    onClick={handleFeatureClick} featuresFilter={featuresFilter}
                    title="Features" showEmpty={true} />
            }
        </Box>
    )
}

export default LauncherFeatureSelector;
